'use client';
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/app/lib/logger/index';
import { getCookie, setCookie } from 'cookies-next';
import { decrypt, encrypt } from '../helpers/cryptoMethods';
import { cookieOptions } from '../helpers/cookiesMethod';
import { setOnlyAppUser, getAppUser, getAppAccessToken, getAppRefreshToken, getAppFirebaseToken, isAppBridge } from '../helpers/appStorageMethod';
import UserModel from '../models/user/UserModel';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import AuthUser from '../models/auth/AuthUser';
import { EditProfileRequest } from '../models/user/IUser';
import moment from 'moment';
export interface UserContextValue {
    auth: AuthUser | null;
    user: UserModel | null;
    error: string | null;
    isLoading: boolean;
    updateDataFromCookiesForProvider?: () => Promise<void>;
    checkPermissions: () => Promise<any>;
    setUser: (data: EditProfileRequest) => void;
}

export interface UserProviderProps {
    children: ReactNode;
}

export const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider = ({ children }: UserProviderProps): JSX.Element => {
    const [state, setState] = useState<{ user: UserModel | null; auth: AuthUser | null; error: string | null; isLoading: boolean }>({
        user: null,
        auth: null,
        error: null,
        isLoading: true,
    });
    // Track nếu đã load xong lần đầu - để không block render các lần sau
    const hasInitialized = useRef(false);

    const checkPermissions = async (): Promise<any> => {
        let hasPermission = false;
        if (!state.auth?.id) {
            hasPermission = false;
        } else if (state.auth?.id && !state.auth?.isRegistered) {
            hasPermission = false;
        } else if (state.auth?.id && state.auth?.isRegistered) {
            hasPermission = true;
        }
        return hasPermission;
    };

    const updateDataFromCookiesForProvider = useCallback(async (): Promise<void> => {
        try {
            // Chỉ set isLoading:true ở lần tải đầu tiên để tránh unmount children gây nháy màn hình
            if (!hasInitialized.current) {
                setState((prev) => ({ ...prev, isLoading: true }));
            }
            const cookieUser = getCookie('user');
            let _accessToken = getCookie('_access');
            let auth = null;
            let user = null;
            let userData = null;

            if (cookieUser) {
                const decryptedUser = decrypt(cookieUser, process.env.SECRET_KEY);
                userData = decryptedUser ? JSON.parse(decryptedUser) : null;
            } else if (isAppBridge()) {
                // Fallback for mobile app: Try to get user from app bridge storage
                userData = await getAppUser();
                
                // If found in app bridge, sync back to cookies for other services (optional but recommended)
                if (userData) {
                    const encryptedUser = encrypt(JSON.stringify(userData), process.env.SECRET_KEY);
                    setCookie('user', encryptedUser, cookieOptions);

                    // Sync tokens as well
                    const appAccessToken = await getAppAccessToken();
                    const appRefreshToken = await getAppRefreshToken();
                    const appFbToken = await getAppFirebaseToken();

                    if (appAccessToken) {
                        const encryptedAccess = encrypt(JSON.stringify(appAccessToken), process.env.SECRET_KEY);
                        setCookie('_access', encryptedAccess, cookieOptions);
                        _accessToken = encryptedAccess; // Update local variable for auth object creation
                    }
                    if (appRefreshToken) {
                        const encryptedRefresh = encrypt(JSON.stringify(appRefreshToken), process.env.SECRET_KEY);
                        setCookie('_refresh', encryptedRefresh, cookieOptions);
                    }
                    if (appFbToken) {
                        const encryptedFb = encrypt(JSON.stringify(appFbToken), process.env.SECRET_KEY);
                        setCookie('_fb_token', encryptedFb, cookieOptions);
                    }
                }
            }

            if (userData) {
                user = new UserModel({
                          id: userData.id,
                          nickname: userData.nickname || '',
                          email: userData.email || undefined,
                          contacts: userData.contacts ?? [],
                          is_staff: userData.isStaff ?? null,
                          is_influencer: userData.isInfluencer ?? null,
                          is_expert: userData.isExpert ?? null,
                          is_google_logged_in: userData.isGoogleLoggedIn ?? false,
                          is_apple_logged_in: userData.isAppleLoggedIn ?? false,
                          is_kakao_logged_in: userData.isKakaoLoggedIn ?? false,
                          is_naver_logged_in: userData.isNaverLoggedIn ?? false,
                          avatar: userData.avatarUrl ?? null,
                          phone_number: userData.phoneNumber ?? null,
                          updated_at: userData.updatedAt ? moment(userData.updatedAt) : moment(),
                          created_at: userData.createdAt ? moment(userData.createdAt) : moment(),
                          level: userData.level,
                          provider: userData.provider ?? null,
                          kakaoId: userData.kakaoId ?? null,
                          naverId: userData.naverId ?? null,
                      });
                if (_accessToken) {
                    let cleanAccessToken = '';
                    try {
                        const decrypted = decrypt(_accessToken as string, process.env.SECRET_KEY);
                        if (decrypted) {
                            try {
                                const parsed = JSON.parse(decrypted);
                                cleanAccessToken = typeof parsed === 'string' ? parsed : decrypted;
                            } catch (e) {
                                cleanAccessToken = decrypted;
                            }
                        }
                    } catch (e) {
                        console.error('Failed to decrypt access token in UserContext', e);
                    }

                    // Làm sạch Token triệt để
                    if (cleanAccessToken) {
                        cleanAccessToken = cleanAccessToken.replace(/^"|"$/g, '').trim();
                    }

                    const authObject = {
                        accessToken: cleanAccessToken,
                        id: userData.id,
                        username: user?.nickname,
                        isRegistered: true,
                    };
                    auth = userData && cleanAccessToken ? new AuthUser(authObject) : null;
                }
            }
            hasInitialized.current = true;
            setState((prev) => ({ ...prev, user, auth, isLoading: false }));
        } catch (error) {
            hasInitialized.current = true;
            logger.error('Error fetching user data from cookies:', error);
            setState((prev) => ({ ...prev, isLoading: false, error: 'Failed to load user data', user: null, auth: null }));
        }
    }, []);

    const setUser = ({ nickname, avatar, level, ...other }: any) => {
        setState((prev: any) => {
            const updatedUser = prev.user ? { ...prev.user, nickname, avatarUrl: avatar, level, ...other } : null;

            if (updatedUser) {
                const encryptedUser = encrypt(JSON.stringify(updatedUser), process.env.SECRET_KEY);
                setCookie('user', encryptedUser, cookieOptions);

                // Sync user data to app storage (app bridge)
                setOnlyAppUser({ user: updatedUser }).catch(() => {
                    // Silently fail if app bridge not available
                });
            }

            return {
                ...prev,
                user: updatedUser,
            };
        });
    };

    useEffect(() => {
        updateDataFromCookiesForProvider?.();
    }, [updateDataFromCookiesForProvider]);

    // Chỉ block render ở lần tải đầu tiên - sau đó không bao giờ unmount children nữa
    if (state.isLoading && !hasInitialized.current) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <UserContext.Provider value={{ ...state, updateDataFromCookiesForProvider, checkPermissions, setUser }}>{children}</UserContext.Provider>
    );
};

export const UserConsumer = UserContext.Consumer;